/**
 * CR500 Phase 2F: Parity Compare Mode (Enhanced)
 * CR500 Phase 5A: Details surface parity comparison
 * CR500 Phase 5B: Details content parity + section order signature
 * CR500 Phase 5C: V2 Readiness Gate panel
 * 
 * Admin-only component that renders V1 and V2 side-by-side for comparison.
 * Activated with ?dealCardV2=1&compare=1
 * 
 * Features:
 * - PASS/FAIL parity summary per card
 * - Spec coverage audit (requested vs rendered vs skipped)
 * - Phase 2-5C completion gate status
 * - V2 Readiness Gate for rollout decision
 * - ?openDetails=1 auto-opens details on both sides for testing
 * - Details section order signature comparison
 */

import { useMemo, useState } from "react";
import { 
  CheckCircle2, 
  XCircle, 
  AlertCircle, 
  ChevronDown, 
  ChevronRight, 
  Info, 
  Rocket, 
  AlertTriangle,
  Shield,
} from "lucide-react";
import type { Deal } from "@/lib/types/deals";
import type { ResolvedDealCardSpecV2 } from "@/lib/deal-cards/specV2/types";
import { DealGiftCard } from "@/components/deals/DealGiftCard";
import { DealCardRendererV2 } from "@/components/deal-cards/DealCardRendererV2";
import { getEnabledBlocks } from "@/lib/deal-cards/specV2/resolveSpec";
import { Badge } from "@/components/ui/badge";
import { 
  generateParityChecklist, 
  formatChecklistCompact,
  type ParityChecklistResult,
  PHASE_2_REQUIRED_BLOCKS,
  PHASE_3_DEFERRED_BLOCKS,
  PHASE_4A_BLOCKS,
  PHASE_4B_BLOCKS,
  PHASE_4C_BLOCKS,
  PHASE_5A_BLOCKS,
  PHASE_5B_BLOCKS,
} from "@/lib/deal-cards/specV2/parityChecklist";
import { preAuditSpec } from "@/lib/deal-cards/specV2/specAudit";
import { DetailsBlocksRenderer } from "@/components/deal-cards/presentation/DetailsBlocksRenderer";
import { DealDetailsContent } from "@/components/deals/DealDetailsContent";
import { DEFAULT_DETAILS_SECTIONS } from "@/lib/layout-studio/dealCardUXTypes";
import { 
  isV2DebugEnabled, 
  isOpenDetailsModeEnabled,
  computeV2ReadinessGate,
  KNOWN_DEFERRALS,
  type V2ReadinessGateResult,
} from "@/lib/deal-cards/helpers";
import { cn } from "@/lib/utils";

interface ParityCompareViewProps {
  deal: Deal & { id?: string; dedupe_key?: string };
  spec: ResolvedDealCardSpecV2;
  isAdmin?: boolean;
}

/**
 * Check if compare mode is enabled via query param.
 */
export function isCompareModeEnabled(): boolean {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  return params.get("compare") === "1";
}

/**
 * PASS/FAIL status badge component.
 */
function ParityStatusBadge({ status }: { status: "PASS" | "FAIL" }) {
  return (
    <Badge 
      variant={status === "PASS" ? "default" : "destructive"} 
      className={cn(
        "text-sm font-bold px-3 py-1",
        status === "PASS" 
          ? "bg-green-600 hover:bg-green-700 text-white" 
          : "bg-red-600 hover:bg-red-700 text-white"
      )}
    >
      {status === "PASS" ? "✅ PASS" : "❌ FAIL"}
    </Badge>
  );
}

/**
 * Compact parity checklist display with PASS/FAIL.
 */
function ParityChecklist({ result }: { result: ParityChecklistResult }) {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <div className="bg-background border border-border rounded-lg p-3">
      {/* Header with PASS/FAIL */}
      <div className="flex items-center justify-between mb-2">
        <button 
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-2 text-xs font-semibold text-foreground hover:text-primary transition-colors"
        >
          {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          Parity Checklist
        </button>
        <div className="flex items-center gap-2">
          <Badge 
            variant="outline" 
            className="text-xs"
          >
            {result.passedCount}/{result.totalCount}
          </Badge>
          <ParityStatusBadge status={result.overallStatus} />
        </div>
      </div>
      
      {/* Failure reasons (always visible if FAIL) */}
      {result.overallStatus === "FAIL" && result.failureReasons.length > 0 && (
        <div className="bg-destructive/10 border border-destructive/20 rounded p-2 mb-2">
          <div className="flex items-start gap-1.5 text-xs text-destructive">
            <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
            <div className="space-y-1">
              <span className="font-medium">Failure Reasons:</span>
              <ul className="list-disc list-inside space-y-0.5">
                {result.failureReasons.map((reason, i) => (
                  <li key={i}>{reason}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
      
      {/* Expandable checklist details */}
      {expanded && (
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 pt-2 border-t border-border">
          {result.checks.map((check) => (
            <div key={check.id} className="flex items-center gap-1.5 text-xs">
              {check.passed ? (
                <CheckCircle2 className="w-3 h-3 text-green-600 flex-shrink-0" />
              ) : (
                <XCircle className={cn(
                  "w-3 h-3 flex-shrink-0",
                  check.severity === "critical" ? "text-red-600" :
                  check.severity === "major" ? "text-orange-500" : "text-yellow-500"
                )} />
              )}
              <span className={check.passed ? "text-muted-foreground" : "text-destructive font-medium"}>
                {check.label}
              </span>
              <Badge variant="outline" className="text-[9px] px-1 py-0">
                {check.severity}
              </Badge>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Spec coverage audit display (admin-only, debug/compare mode).
 */
function SpecAuditPanel({ spec }: { spec: ResolvedDealCardSpecV2 }) {
  const debugMode = isV2DebugEnabled();
  const [expanded, setExpanded] = useState(false);
  
  const primaryAudit = useMemo(
    () => preAuditSpec(spec, "primary", debugMode),
    [spec, debugMode]
  );
  
  const detailsAudit = useMemo(
    () => preAuditSpec(spec, "details", debugMode),
    [spec, debugMode]
  );
  
  return (
    <div className="bg-muted/30 border border-border rounded-lg p-3">
      <button 
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-xs font-semibold text-foreground hover:text-primary transition-colors w-full"
      >
        {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        <span>Spec Coverage Audit</span>
        <Badge variant="outline" className="text-xs ml-auto">
          {primaryAudit.enabledBlocks.length + detailsAudit.enabledBlocks.length} blocks enabled
        </Badge>
      </button>
      
      {expanded && (
        <div className="space-y-3 pt-3 border-t border-border mt-2">
          {/* Primary surface */}
          <div className="space-y-1">
            <span className="text-xs font-medium text-muted-foreground">Primary Surface:</span>
            <div className="flex flex-wrap gap-1">
              {primaryAudit.enabledBlocks.map((block) => (
                <Badge key={block.id} variant="default" className="text-xs bg-green-600/80">
                  {block.id}
                </Badge>
              ))}
              {primaryAudit.placeholderBlocks.map((block) => (
                <Badge key={block.id} variant="outline" className="text-xs text-orange-500 border-orange-500/50">
                  {block.id} (Phase 3)
                </Badge>
              ))}
            </div>
          </div>
          
          {/* Details surface */}
          <div className="space-y-1">
            <span className="text-xs font-medium text-muted-foreground">Details Surface:</span>
            <div className="flex flex-wrap gap-1">
              {detailsAudit.enabledBlocks.map((block) => (
                <Badge key={block.id} variant="secondary" className="text-xs">
                  {block.id}
                </Badge>
              ))}
            </div>
          </div>
          
          {/* Warnings */}
          {(primaryAudit.warnings.length > 0 || detailsAudit.warnings.length > 0) && (
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded p-2">
              <div className="flex items-start gap-1.5 text-xs text-yellow-600">
                <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                <div className="space-y-0.5">
                  {[...primaryAudit.warnings, ...detailsAudit.warnings].map((w, i) => (
                    <div key={i}>{w}</div>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          {/* Phase gate info */}
          <div className="text-xs text-muted-foreground space-y-1 pt-2 border-t border-border">
            <div className="flex items-center gap-2">
              <span className="font-medium">Phase 2 Required:</span>
              <span>{PHASE_2_REQUIRED_BLOCKS.join(", ")}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-medium">Phase 3:</span>
              <span className="text-green-600">{PHASE_3_DEFERRED_BLOCKS.join(", ")}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-medium">Phase 4A:</span>
              <span className="text-blue-600">{PHASE_4A_BLOCKS.join(", ")}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-medium">Phase 4B:</span>
              <span className="text-purple-600">{PHASE_4B_BLOCKS.join(", ")}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-medium">Phase 4C:</span>
              <span className="text-emerald-600">{PHASE_4C_BLOCKS.join(", ")}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-medium">Phase 5A (Details Surface):</span>
              <span className="text-orange-600">{PHASE_5A_BLOCKS.join(", ")}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-medium">Phase 5B (Details Content):</span>
              <span className="text-pink-600">{PHASE_5B_BLOCKS.join(", ")}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-medium">Phase 5C (Rollout Gate):</span>
              <span className="text-cyan-600">readiness_gate, debug_hardening</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * V2 Readiness Gate Panel (Phase 5C)
 * Shows ship-readiness checklist for V2 rollout decision.
 */
function V2ReadinessGatePanel() {
  const [expanded, setExpanded] = useState(true);
  
  const readinessResult = useMemo(() => computeV2ReadinessGate(), []);
  
  const categoryLabels: Record<string, { label: string; icon: React.ReactNode }> = {
    front: { label: "Front Face", icon: <CheckCircle2 className="w-3 h-3" /> },
    badges: { label: "Badges", icon: <Shield className="w-3 h-3" /> },
    details: { label: "Details", icon: <Info className="w-3 h-3" /> },
    debug: { label: "Debug", icon: <AlertTriangle className="w-3 h-3" /> },
  };
  
  return (
    <div className={cn(
      "border rounded-lg p-3",
      readinessResult.overallReady 
        ? "bg-green-500/10 border-green-500/30" 
        : "bg-amber-500/10 border-amber-500/30"
    )}>
      <button 
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-sm font-semibold w-full"
      >
        {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        <Rocket className="w-4 h-4" />
        <span>V2 Readiness Gate</span>
        <Badge 
          variant={readinessResult.overallReady ? "default" : "secondary"} 
          className={cn(
            "ml-auto text-xs font-bold",
            readinessResult.overallReady 
              ? "bg-green-600 text-white" 
              : "bg-amber-600 text-white"
          )}
        >
          {readinessResult.overallReady ? "✅ READY" : "⚠️ NOT READY"}
        </Badge>
      </button>
      
      {expanded && (
        <div className="space-y-3 pt-3 border-t border-border/50 mt-2">
          {/* Checklist */}
          <div className="grid grid-cols-2 gap-2">
            {readinessResult.checks.map((check) => {
              const cat = categoryLabels[check.category];
              return (
                <div 
                  key={check.id}
                  className={cn(
                    "flex items-start gap-2 p-2 rounded text-xs",
                    check.passed 
                      ? "bg-green-500/10 text-green-700" 
                      : "bg-red-500/10 text-red-700"
                  )}
                >
                  {check.passed ? (
                    <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  ) : (
                    <XCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  )}
                  <div>
                    <div className="font-medium">{check.label}</div>
                    {check.details && (
                      <div className="text-[10px] opacity-80 mt-0.5">{check.details}</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Summary */}
          <div className="flex items-center justify-between text-xs border-t border-border/50 pt-2">
            <span className="text-muted-foreground">
              {readinessResult.passedCount}/{readinessResult.totalCount} checks passed
            </span>
            {readinessResult.overallReady && (
              <span className="text-green-600 font-medium">
                Safe to enable V2 default
              </span>
            )}
          </div>
          
          {/* Known deferrals */}
          <div className="bg-muted/50 rounded p-2">
            <div className="text-xs font-medium text-muted-foreground mb-1">
              Known Deferrals (don't block rollout):
            </div>
            <ul className="text-xs text-muted-foreground space-y-0.5">
              {KNOWN_DEFERRALS.map((item, i) => (
                <li key={i} className="flex items-center gap-1">
                  <span className="text-amber-500">•</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Side-by-side comparison of V1 and V2 renderers with parity checklist.
 */
export function ParityCompareView({ deal, spec, isAdmin = false }: ParityCompareViewProps) {
  const enabledPrimaryBlocks = getEnabledBlocks(spec, "primary");
  const enabledDetailsBlocks = getEnabledBlocks(spec, "details");
  
  // Check if details should be auto-opened
  const openDetailsMode = isOpenDetailsModeEnabled();
  const [showDetailsComparison, setShowDetailsComparison] = useState(openDetailsMode);
  
  // Generate parity checklist
  const parityChecklist = useMemo(
    () => generateParityChecklist(deal),
    [deal]
  );
  
  // Log compact checklist for easy scanning
  if (isAdmin && typeof window !== "undefined") {
    console.debug(
      `[Parity] ${deal.id || deal.dedupe_key}: ${formatChecklistCompact(parityChecklist)}`
    );
  }
  
  return (
    <div className="bg-muted/30 border border-border rounded-lg p-4 space-y-4">
      {/* Header with PASS/FAIL status */}
      <div className="flex items-center justify-between border-b border-border pb-2">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-semibold text-foreground">Parity Compare Mode</h3>
          <ParityStatusBadge status={parityChecklist.overallStatus} />
        </div>
        <div className="flex gap-2">
          <Badge variant="outline" className="text-xs">
            {spec.resolved.dealTypeKey}
          </Badge>
          <Badge variant="secondary" className="text-xs">
            {spec.resolved.variantKey}
          </Badge>
        </div>
      </div>
      
      {/* Parity Checklist with PASS/FAIL */}
      <ParityChecklist result={parityChecklist} />
      
      {/* V2 Readiness Gate (Phase 5C) */}
      <V2ReadinessGatePanel />
      
      {/* Spec Coverage Audit */}
      <SpecAuditPanel spec={spec} />
      
      {/* Side-by-side comparison - Primary Surface */}
      <div className="grid grid-cols-2 gap-4">
        {/* V1 */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-muted-foreground">V1 Renderer</span>
            <Badge variant="outline" className="text-xs bg-muted text-muted-foreground border-border">
              Legacy
            </Badge>
          </div>
          <div className="border border-border rounded-lg overflow-hidden bg-background">
            <DealGiftCard deal={deal} isAdmin={isAdmin} />
          </div>
        </div>
        
        {/* V2 */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-muted-foreground">V2 Renderer</span>
            <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary/20">
              SpecV2
            </Badge>
          </div>
          <div className="border border-border rounded-lg overflow-hidden bg-background p-4">
            <DealCardRendererV2
              deal={deal}
              spec={spec}
              isAdmin={isAdmin}
              surface="primary"
              debug={false}
            />
          </div>
        </div>
      </div>
      
      {/* Details Surface Comparison (Phase 5A/5B) */}
      <div className="border-t border-border pt-3 space-y-3">
        <button
          onClick={() => setShowDetailsComparison(!showDetailsComparison)}
          className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary transition-colors"
        >
          {showDetailsComparison ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          Details Surface Comparison (Phase 5A/5B)
          <Badge variant="outline" className="text-xs bg-orange-500/10 text-orange-600 border-orange-500/20">
            Back Face
          </Badge>
        </button>
        
        {showDetailsComparison && (
          <>
            {/* Section order signature comparison */}
            <div className="bg-muted/30 border border-border rounded p-3 space-y-2">
              <div className="text-xs font-medium text-foreground flex items-center gap-2">
                Section Order Signature (Phase 5B)
                <Badge variant="outline" className="text-xs bg-pink-500/10 text-pink-600 border-pink-500/20">
                  Parity Check
                </Badge>
              </div>
              <div className="grid grid-cols-2 gap-4 text-xs">
                <div>
                  <span className="text-muted-foreground">V1 Order:</span>
                  <div className="font-mono text-foreground mt-0.5">
                    terms → fine_print → source → evidence → validator → actions
                  </div>
                </div>
                <div>
                  <span className="text-muted-foreground">V2 Order:</span>
                  <div className="font-mono text-foreground mt-0.5">
                    {enabledDetailsBlocks.map(b => b.id).join(" → ") || "(none)"}
                  </div>
                </div>
              </div>
              
              {/* Section-level parity checks */}
              <div className="flex flex-wrap gap-2 pt-2 border-t border-border/50">
                {parityChecklist.checks
                  .filter(c => c.category === "details" || c.category === "details_order")
                  .map((check) => (
                    <div 
                      key={check.id} 
                      className={cn(
                        "flex items-center gap-1 text-xs px-2 py-0.5 rounded",
                        check.passed ? "bg-green-500/10 text-green-600" : "bg-red-500/10 text-red-600"
                      )}
                    >
                      {check.passed ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                      {check.label}
                    </div>
                  ))}
              </div>
            </div>
          
            <div className="grid grid-cols-2 gap-4">
              {/* V1 Details */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-muted-foreground">V1 Details</span>
                  <Badge variant="outline" className="text-xs bg-muted text-muted-foreground border-border">
                    Legacy
                  </Badge>
                </div>
                <div className="border border-border rounded-lg overflow-hidden bg-card p-3 max-h-[300px] overflow-y-auto">
                  <DealDetailsContent
                    deal={deal}
                    sectionsConfig={DEFAULT_DETAILS_SECTIONS}
                    mode="flip"
                    isAdmin={isAdmin}
                  />
                </div>
              </div>
              
              {/* V2 Details */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-muted-foreground">V2 Details</span>
                  <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary/20">
                    SpecV2
                  </Badge>
                </div>
                <div className="border border-border rounded-lg overflow-hidden bg-card p-3 max-h-[300px] overflow-y-auto">
                  <DetailsBlocksRenderer
                    deal={deal}
                    spec={spec}
                    isAdmin={isAdmin}
                    mode="flip"
                  />
                </div>
              </div>
            </div>
          </>
        )}
        
        {/* Details parity tip */}
        <div className="text-xs text-muted-foreground bg-muted/50 p-2 rounded">
          <strong>Tip:</strong> Use <code className="bg-muted px-1 rounded">?openDetails=1</code> to auto-expand this comparison.
          Check that both sides show: Header, Offer Terms, Source, and Action buttons with matching content.
        </div>
      </div>
      
      {/* Block visibility summary */}
      <div className="border-t border-border pt-3 space-y-2">
        <p className="text-xs font-medium text-muted-foreground">V2 Blocks Enabled:</p>
        <div className="flex flex-wrap gap-1">
          <span className="text-xs text-muted-foreground mr-1">Primary:</span>
          {enabledPrimaryBlocks.map((block) => (
            <Badge key={block.id} variant="secondary" className="text-xs">
              {block.id}
            </Badge>
          ))}
        </div>
        <div className="flex flex-wrap gap-1">
          <span className="text-xs text-muted-foreground mr-1">Details:</span>
          {enabledDetailsBlocks.map((block) => (
            <Badge key={block.id} variant="outline" className="text-xs">
              {block.id}
            </Badge>
          ))}
        </div>
        <div className="text-xs text-muted-foreground">
          Variant: {spec.resolved.variantKey} • 
          Fallback: {spec.resolved.usedFallback ? "Yes" : "No"} • 
          Sources: variant={spec.resolved.sources.variant}, overrides={spec.resolved.sources.overrides}
        </div>
      </div>
    </div>
  );
}

export default ParityCompareView;
